import type { ReactNode } from "react";
import type { ChoiceCategory } from "../scenario/types";

/**
 * Unified "response category" decision card used by EVERY interactive dialogue
 * across all 3D courses (IT call, deepfake video call, intercom, tech request,
 * son call, …). Instead of a plain button, each reply is shown as a clear
 * category card: a coloured icon chip + the learner's reply + a category label
 * with its risk meaning, so the learner learns to recognise the KIND of move
 * they are making.
 *
 * Four categories (colours chosen to stay legible on the dark device cards —
 * bright tints + a solid icon chip, never dark-on-dark):
 *   verify   — green shield  — pause & verify (safe)
 *   question — blue question — ask for more context (neutral)
 *   refuse   — red stop      — reject the unsafe action (stop)
 *   comply   — amber warning — follow the attacker's instruction (risky)
 */

interface CatMeta {
  /** primary category word (rendered uppercase via CSS). */
  label: string;
  /** default risk meaning shown after the label. */
  meaning: string;
  icon: ReactNode;
}

const ShieldIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const QuestionIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.2 9.2a2.8 2.8 0 0 1 5.4 1c0 1.8-2.6 2.2-2.6 3.8" />
    <circle cx="12" cy="17.4" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

const StopIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M8.2 3h7.6L21 8.2v7.6L15.8 21H8.2L3 15.8V8.2L8.2 3z" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </svg>
);

const WarnIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3.8L21.2 20H2.8L12 3.8z" />
    <path d="M12 10v4.5" />
    <circle cx="12" cy="17.6" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

export const CHOICE_META: Record<ChoiceCategory, CatMeta> = {
  verify: { label: "Verify", meaning: "Safe", icon: ShieldIcon },
  question: { label: "Question", meaning: "Neutral", icon: QuestionIcon },
  refuse: { label: "Refuse", meaning: "Stop", icon: StopIcon },
  comply: { label: "Comply", meaning: "Risky", icon: WarnIcon },
};

export function ChoiceCard({
  category,
  reply,
  meaning,
  onClick,
  disabled,
  /** When false, hide VERIFY/RISKY labels so the learner decides without spoilers.
   *  Use for awareness quizzes / pack / Wi-Fi / etc. Keep true for live attack
   *  dialogues where recognising the move type IS the lesson. */
  showRiskLabel = true,
}: {
  category: ChoiceCategory;
  /** the learner's reply / action text shown as the card's main line. */
  reply: string;
  /** optional override of the risk meaning shown after the category label. */
  meaning?: string;
  onClick: () => void;
  disabled?: boolean;
  showRiskLabel?: boolean;
}) {
  const m = CHOICE_META[category];
  if (!showRiskLabel) {
    return (
      <button
        type="button"
        className="cf-choice cf-choice--plain"
        onClick={onClick}
        disabled={disabled}
      >
        <span className="cf-choice-body">
          <span className="cf-choice-reply">{reply}</span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`cf-choice cf-choice--${category}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="cf-choice-ico" aria-hidden>
        {m.icon}
      </span>
      <span className="cf-choice-body">
        <span className="cf-choice-reply">{reply}</span>
        <span className="cf-choice-cat">
          {m.label}
          <i>·</i>
          {meaning ?? m.meaning}
        </span>
      </span>
    </button>
  );
}

export function ChoiceList({ prompt, children }: { prompt?: ReactNode; children: ReactNode }) {
  return (
    <div className="cf-choices">
      {prompt && <p className="cf-choices-q">{prompt}</p>}
      <div className="cf-choices-list">{children}</div>
    </div>
  );
}
